import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
import { IAddManualPrinterParams } from '@/interfaces/print-bridge-protocol';
import { PrintSource, TicketPrintSetting } from '@/enums/PrintEnum';
import type {
  IZplPrinterSettings,
  PrinterCutType,
  PrinterModel,
  QrRenderMode,
} from '@/lib/print/printer-settings';

/**
 * Schema of THE document stored on the bridge's config store (opaque to the
 * bridge). ADMIN-OWNED: deliberately NOT part of the vendored keep-in-sync
 * protocol surface — the bridge never interprets it.
 */

export const PRINTING_V2_SCHEMA_VERSION = 1;

/**
 * 'modern' (default) cuts with GS V 65/66 0 (feed to cut position, then cut).
 * 'legacy' is the fallback for firmwares that ignore that command: extra
 * feed lines followed by the legacy ESC i/m in-place cut.
 */
export type PrinterCutMode = 'modern' | 'legacy';

export interface ILogicalPrinter {
  /** incremental, unique within the document */
  id: number;
  /** user label, e.g. "Impresora de caja" */
  name: string;
  /** bridge printer ref */
  ref: string;
  transport: 'spooler' | 'tcp' | 'serial-bt';
  format: 'escpos' | 'zpl';
  /** escpos only */
  paperWidthMm: 58 | 80;
  /** escpos only, default 'cp858' */
  codepage: string;
  /** escpos only; absent = 'modern' (field added post-launch) */
  cutMode?: PrinterCutMode;
  // Per-printer hardware settings (all added post-launch, same optional-field
  // precedent as cutMode — NO schema bump; older builds preserve unknown
  // fields through mutateConfig's cloneDeep drafts and verbatim adopt).
  /** escpos only; absent = 'generic' */
  model?: PrinterModel;
  /** escpos only; absent = 'auto' */
  qrMode?: QrRenderMode;
  /** escpos only; absent = auto (width table) */
  charsPerLineNormal?: number | null;
  /** escpos only; absent = auto (width table) */
  charsPerLineSmall?: number | null;
  /** escpos only; absent = 'partial' */
  cutType?: PrinterCutType;
  /** escpos only; absent/null = each document's historical feed count */
  feedLines?: number | null;
  /**
   * zpl only; absent = resolved at PRINT time from the legacy localStorage
   * stickerSettings (then defaults) — preserves pre-existing printers'
   * staleness behavior.
   */
  zpl?: IZplPrinterSettings;
}

export interface IUnresolvedV1Assignment {
  destination: 'ticket' | 'sticker' | 'comanda';
  /** comanda only */
  stationId?: number;
  /** the v1 spooler name that no longer resolves */
  v1Name: string;
}

export interface IPrintingV2Config {
  schemaVersion: number;
  /** epoch ms, stamped by the admin on every accepted mutation */
  savedAt: number;
  logicalPrinters: ILogicalPrinter[];
  assignments: {
    ticket: number | null;
    sticker: number | null;
    comandaByStation: Record<number, number | null>;
  };
  /**
   * The legacy per-destination settings (triggers, source, etc.) — stored in
   * the document so auto-print triggers ALSO survive browser-data clearing.
   * Their `encoding` field is dead in v2: the assigned logical printer's
   * codepage wins on projection.
   */
  destinationSettings: {
    ticket: IPrintingSettings;
    comanda: IPrintingSettings;
    sticker: IPrintingSettings;
  };
  unresolvedV1: IUnresolvedV1Assignment[];
  migratedFromV1: boolean;
}

export const DEFAULT_CODEPAGE = 'cp858';

/** One destination checkbox in the assignment picker / wizard StepAssign. */
export interface IDestinationSelection {
  destination: 'ticket' | 'sticker' | 'comanda';
  /** comanda only */
  stationId?: number;
}

/**
 * Applies a picker selection to a draft document: checked destinations point
 * at `logicalId` (replacing any previous printer); unchecked destinations
 * that currently point at `logicalId` are cleared. Other printers'
 * assignments are untouched.
 */
export function applyAssignmentSelection(
  draft: IPrintingV2Config,
  logicalId: number,
  selected: IDestinationSelection[]
): void {
  const has = (destination: string, stationId?: number) =>
    selected.some(
      (s) => s.destination === destination && s.stationId === stationId
    );

  if (has('ticket')) draft.assignments.ticket = logicalId;
  else if (draft.assignments.ticket === logicalId) {
    draft.assignments.ticket = null;
  }

  if (has('sticker')) draft.assignments.sticker = logicalId;
  else if (draft.assignments.sticker === logicalId) {
    draft.assignments.sticker = null;
  }

  const stationIds = new Set<number>([
    ...Object.keys(draft.assignments.comandaByStation).map(Number),
    ...selected
      .filter((s) => s.destination === 'comanda' && s.stationId != null)
      .map((s) => s.stationId as number),
  ]);
  for (const stationId of stationIds) {
    if (has('comanda', stationId)) {
      draft.assignments.comandaByStation = {
        ...draft.assignments.comandaByStation,
        [stationId]: logicalId,
      };
    } else if (draft.assignments.comandaByStation[stationId] === logicalId) {
      draft.assignments.comandaByStation = {
        ...draft.assignments.comandaByStation,
        [stationId]: null,
      };
    }
  }
}

export function getDefaultDestinationSettings(): IPrintingSettings {
  return {
    trigger: TicketPrintSetting.Manual,
    source: PrintSource.ThisDevice,
    encoding: DEFAULT_CODEPAGE,
  };
}

export function createEmptyPrintingV2Config(): IPrintingV2Config {
  return {
    schemaVersion: PRINTING_V2_SCHEMA_VERSION,
    savedAt: Date.now(),
    logicalPrinters: [],
    assignments: { ticket: null, sticker: null, comandaByStation: {} },
    destinationSettings: {
      ticket: getDefaultDestinationSettings(),
      comanda: getDefaultDestinationSettings(),
      sticker: getDefaultDestinationSettings(),
    },
    unresolvedV1: [],
    migratedFromV1: false,
  };
}

/** Minimal shape check for a document adopted from the bridge. */
export function isPrintingV2Config(value: unknown): value is IPrintingV2Config {
  if (typeof value !== 'object' || value === null) return false;
  const doc = value as Partial<IPrintingV2Config>;
  return (
    typeof doc.schemaVersion === 'number' &&
    Array.isArray(doc.logicalPrinters) &&
    typeof doc.assignments === 'object' &&
    doc.assignments !== null
  );
}

/** "Non-empty" per the hydration decision matrix (B3). */
export function isConfigNonEmpty(config: IPrintingV2Config | null): boolean {
  if (!config) return false;
  return (
    config.logicalPrinters.length > 0 ||
    config.unresolvedV1.length > 0 ||
    config.migratedFromV1
  );
}

/**
 * Rebuilds `printers.addManual` params from a manual printer ref, for the
 * post-push-up reconciliation (a bridge reinstall wipes its manual entries;
 * the restored config would otherwise point at ghosts).
 * Refs: 'tcp:<host>:<port>' | 'bt:<COMn>'.
 */
export function manualPrinterParamsFromRef(
  logical: Pick<ILogicalPrinter, 'ref' | 'transport' | 'name'>
): IAddManualPrinterParams | null {
  if (logical.transport === 'tcp' && logical.ref.startsWith('tcp:')) {
    const rest = logical.ref.slice('tcp:'.length);
    const sep = rest.lastIndexOf(':');
    if (sep <= 0) return null;
    const host = rest.slice(0, sep);
    const port = Number(rest.slice(sep + 1));
    if (!host || !Number.isInteger(port) || port <= 0) return null;
    return { transport: 'tcp', host, port, name: logical.name, probe: false };
  }

  if (logical.transport === 'serial-bt' && logical.ref.startsWith('bt:')) {
    const comPort = logical.ref.slice('bt:'.length);
    if (!comPort) return null;
    return {
      transport: 'serial-bt',
      comPort,
      name: logical.name,
      probe: false,
    };
  }

  return null;
}
